<template>
  <Transition name="fade">
    <div v-if="visible" class="page-load-overlay" :class="{ 'transparent-bg': currentAnimation === 'katana-slice' }">
      <!-- Animation 1: Light Ray -->
      <div v-if="currentAnimation === 'light-ray'" class="light-ray"></div>
      
      <!-- Animation 2: Katana -->
      <div v-if="currentAnimation === 'katana'" class="mover">
        <div class="trail"></div>
        <img src="https://isamur.ai/images/isamurai-katana.webp" class="katana-img" :alt="$t('components.pageLoad.katanaAlt')" />
      </div>

      <!-- Animation 3: Katana Slice (Shatter) -->
      <div v-if="currentAnimation === 'katana-slice'" class="katana-slice-container">
        <!-- Shards -->
        <div 
          v-for="(shard, index) in shards" 
          :key="'shard-' + index"
          class="shard"
          :style="shard.style"
          :class="{ fallen: shard.isFallen }"
        ></div>
        <!-- Cut Lines -->
        <div 
          v-for="(cut, index) in activeCuts" 
          :key="'cut-' + index"
          class="cut-line active"
          :style="cut.style"
        ></div>
      </div>
      
      <!-- Logo with beating animation -->
      <div class="logo-container" :class="{ 'hidden': !logoVisible }">
        <img src="https://isamur.ai/images/logo.webp" :alt="$t('components.pageLoad.loadingAlt')" class="logo-beat" />

        <!-- Default Loading Bar (always visible) -->
        <div class="default-loader">
          <div class="loader-bar">
            <div class="loader-fill"></div>
          </div>
          <p class="loader-text">{{ dataLoading ? loadingText : $t('components.pageLoad.loadingDefault') }}</p>
          <span v-if="dataLoading" class="loader-percent">{{ loadingProgress }}%</span>
        </div>
      </div>
      
      <!-- Data Loading Indicator (circular, only when dataLoading) -->
      <div v-if="dataLoading" class="data-loader-container">
        <!-- SVG Gradient Definition -->
        <svg width="0" height="0">
          <defs>
            <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style="stop-color:#bc13fe"/>
              <stop offset="100%" style="stop-color:#00f3ff"/>
            </linearGradient>
          </defs>
        </svg>
        <div class="progress-ring">
          <svg viewBox="0 0 120 120">
            <circle class="progress-bg" cx="60" cy="60" r="54"/>
            <circle class="progress-bar" cx="60" cy="60" r="54"
              :style="{ strokeDashoffset: progressOffset }"/>
          </svg>
          <span class="progress-text">{{ loadingProgress }}%</span>
        </div>
        <p class="loading-label">{{ loadingText }}</p>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'PageLoad',
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    minDuration: {
      type: Number,
      default: 200
    },
    // Data loading props
    dataLoading: {
      type: Boolean,
      default: false
    },
    loadingProgress: {
      type: Number,
      default: 0
    },
    loadingText: {
      type: String,
      default: 'Loading your data...'
    }
  },
  data() {
    return {
      visible: this.loading, // Initialize based on prop to prevent flash
      startTime: null,
      animations: ['light-ray', 'katana', 'katana-slice'],
      currentAnimation: null,
      // Katana Slice Data
      numberOfCuts: 8,
      shards: [],
      cutLines: [],
      activeCuts: [],
      logoVisible: true
    };
  },
  computed: {
    progressOffset() {
      const circumference = 2 * Math.PI * 54; // 339.292
      return circumference - (this.loadingProgress / 100) * circumference;
    }
  },
  watch: {
    loading: {
      handler(newVal) {
        if (newVal) {
          // Show loading
          this.randomizeAnimation();
          this.visible = true;
          this.startTime = Date.now();
          this.logoVisible = true;
          this.activeCuts = [];
          if (this.currentAnimation === 'katana-slice') {
            this.$nextTick(() => {
              this.generateCuts();
            });
          }
        } else {
          // Hide with minimum duration
          this.hideWithDelay();
        }
      }
    }
  },
  created() {
    // Randomize immediately if starting visible
    if (this.visible) {
      this.randomizeAnimation();
      this.startTime = Date.now();
    }
  },
  mounted() {
    if (this.visible && this.currentAnimation === 'katana-slice') {
      this.generateCuts();
    }
    if (!this.loading && this.visible) {
       this.hideWithDelay();
    }
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      if (this.visible && this.currentAnimation === 'katana-slice') {
        this.generateCuts();
      }
    },
    hideWithDelay() {
      if (!this.startTime) {
        this.visible = false;
        return;
      }
      
      const elapsed = Date.now() - this.startTime;
      const maxWait = 10000; // Max 10 seconds wait for data loading
      
      // If dataLoading is active AND has actually started (progress > 0)
      // AND we haven't waited too long, wait for 100%
      if (this.dataLoading && this.loadingProgress > 0 && this.loadingProgress < 100 && elapsed < maxWait) {
        // Check again in 100ms
        setTimeout(() => this.hideWithDelay(), 100);
        return;
      }
      
      const remaining = Math.max(0, this.minDuration - elapsed);
      
      setTimeout(() => {
        if (this.currentAnimation === 'katana-slice') {
          this.triggerShatter();
        } else {
          this.visible = false;
        }
      }, remaining);
    },
    randomizeAnimation() {
      const randomIndex = Math.floor(Math.random() * this.animations.length);
      this.currentAnimation = this.animations[randomIndex];
    },
    // --- Katana Slice Logic ---
    initShards() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const initialPoly = [[0,0], [w,0], [w,h], [0,h]];
      this.shards = [{
        points: initialPoly,
        style: this.getShardStyle(initialPoly),
        isFallen: false,
        centerX: w/2,
        centerY: h/2
      }];
      this.cutLines = [];
      this.activeCuts = [];
    },
    generateCuts() {
      this.initShards();
      let currentPolys = this.shards.map(s => s.points);
      
      for(let i = 0; i < this.numberOfCuts; i++) {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const cx = w * (0.2 + Math.random() * 0.6);
        const cy = h * (0.2 + Math.random() * 0.6);
        const angle = Math.random() * Math.PI;
        
        const a = Math.sin(angle);
        const b = -Math.cos(angle);
        const c = -(a * cx + b * cy);

        const visualLine = this.calculateScreenIntersections(a, b, c);
        if(visualLine) this.cutLines.push(visualLine);

        let newPolys = [];
        currentPolys.forEach(poly => {
          const result = this.splitPolygon(poly, a, b, c);
          if (result.length > 0) {
            newPolys.push(...result);
          } else {
            newPolys.push(poly);
          }
        });
        currentPolys = newPolys;
      }
      
      this.shards = currentPolys.map(poly => {
        const bounds = this.getPolyBounds(poly);
        return {
          points: poly,
          style: this.getShardStyle(poly),
          isFallen: false,
          centerX: bounds.centerX,
          centerY: bounds.centerY
        };
      });
    },
    splitPolygon(poly, a, b, c) {
      let front = [];
      let back = [];
      
      for (let i = 0; i < poly.length; i++) {
        const p1 = poly[i];
        const p2 = poly[(i + 1) % poly.length];
        const d1 = a * p1[0] + b * p1[1] + c;
        const d2 = a * p2[0] + b * p2[1] + c;
        
        if (d1 >= 0) front.push(p1);
        if (d1 <= 0) back.push(p1);
        
        if ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) {
          const t = d1 / (d1 - d2);
          const ix = p1[0] + t * (p2[0] - p1[0]);
          const iy = p1[1] + t * (p2[1] - p1[1]);
          front.push([ix, iy]);
          back.push([ix, iy]);
        }
      }
      
      const res = [];
      if (front.length >= 3) res.push(front);
      if (back.length >= 3) res.push(back);
      if (res.length < 2) return [];
      return res;
    },
    calculateScreenIntersections(a, b, c) {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const points = [];
      
      if (Math.abs(b) > 0.001) {
        let y = -c / b;
        if (y >= 0 && y <= h) points.push([0, y]);
        y = (-c - a * w) / b;
        if (y >= 0 && y <= h) points.push([w, y]);
      }
      if (Math.abs(a) > 0.001) {
        let x = -c / a;
        if (x >= 0 && x <= w) points.push([x, 0]);
        x = (-c - b * h) / a;
        if (x >= 0 && x <= w) points.push([x, h]);
      }

      if (points.length >= 2) return [points[0], points[points.length-1]];
      return null;
    },
    getPolyBounds(poly) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      poly.forEach(p => {
        if (p[0] < minX) minX = p[0];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[1] > maxY) maxY = p[1];
      });
      return { centerX: (minX + maxX)/2, centerY: (minY + maxY)/2 };
    },
    getShardStyle(points) {
      const pathStr = points.map(p => `${p[0]}px ${p[1]}px`).join(', ');
      return {
        clipPath: `polygon(${pathStr})`,
        webkitClipPath: `polygon(${pathStr})`
      };
    },
    triggerShatter() {
      // 1. Play Cuts sequentially
      this.cutLines.forEach((line, index) => {
        setTimeout(() => {
          const [p1, p2] = line;
          const dx = p2[0] - p1[0];
          const dy = p2[1] - p1[1];
          const angleRad = Math.atan2(dy, dx);
          
          this.activeCuts.push({
            style: {
              left: `${p1[0]}px`,
              top: `${p1[1]}px`,
              transform: `rotate(${angleRad}rad)`
            }
          });
          
          // Shake effect
          document.body.classList.remove('shake-impact');
          void document.body.offsetWidth;
          document.body.classList.add('shake-impact');
          
        }, index * 150);
      });

      // 2. Shatter after cuts
      const totalCutTime = this.cutLines.length * 150;
      setTimeout(() => {
        this.logoVisible = false;
        const screenCx = window.innerWidth / 2;
        
        this.shards.forEach(shard => {
          const dx = (shard.centerX - screenCx) / screenCx;
          const fallY = 500 + Math.random() * 700;
          const slideX = dx * 300;
          const rotate = (Math.random() - 0.5) * 60;
          
          shard.style = {
            ...shard.style,
            transform: `translate(${slideX}px, ${fallY}px) rotate(${rotate}deg)`,
            opacity: 0
          };
          shard.isFallen = true;
        });

        // 3. Cleanup after fall animation
        setTimeout(() => {
          this.visible = false;
          this.shards = [];
          this.activeCuts = [];
          this.logoVisible = true;
          document.body.classList.remove('shake-impact');
        }, 1200);
        
      }, totalCutTime + 100);
    }
  }
}
</script>

<style scoped>
.page-load-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #030014;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  overflow: hidden;
  /* GPU acceleration for smooth fade */
  will-change: opacity;
  transform: translateZ(0);
}

/* ==================== */
/* Animation 1: Light Ray */
/* ==================== */
.light-ray {
  position: absolute;
  top: -50%;
  left: 50%;
  width: 4px;
  height: 250%;
  background: linear-gradient(180deg, transparent 0%, #F900FB 40%, #8C00AF 60%, transparent 100%);
  opacity: 0.7;
  box-shadow: 0 0 20px #F900FB, 0 0 40px #8C00AF;
  transform: translateX(-1000px) rotate(25deg);
  animation: raySwipe 1.2s linear infinite;
  /* GPU acceleration */
  will-change: transform;
  backface-visibility: hidden;
}

@keyframes raySwipe {
  0% { transform: translateX(-1000px) rotate(25deg); }
  100% { transform: translateX(1000px) rotate(25deg); }
}

/* ==================== */
/* Animation 2: Katana */
/* ==================== */
.mover {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: moveDesktop 1.2s linear infinite;
  /* GPU acceleration */
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.katana-img {
  z-index: 10;
  filter: drop-shadow(0 0 10px #bc13fe) drop-shadow(0 0 5px #00f3ff);
  height: 120vh;
  width: auto;
  transform: rotate(12deg);
}

.trail {
  position: absolute;
  z-index: 1;
  filter: blur(15px);
  opacity: 0.8;
  right: 50%;
  top: 50%;
  height: 200vh;
  width: 150vw;
  transform: translateY(-50%);
  background: linear-gradient(to right, transparent 0%, rgba(188, 19, 254, 0.4) 60%, rgba(0, 243, 255, 0.9) 100%);
  mask-image: linear-gradient(to right, transparent, black 80%);
  -webkit-mask-image: linear-gradient(to right, transparent, black 80%);
  /* GPU acceleration */
  will-change: transform;
  backface-visibility: hidden;
}

@keyframes moveDesktop {
  0% { transform: translate(-50%, -50%) translateX(-130vw); }
  100% { transform: translate(-50%, -50%) translateX(130vw); }
}

/* Katana Mobile Styles */
@media (max-width: 768px) {
  .mover {
    animation: moveMobile 1.2s linear infinite;
  }

  @keyframes moveMobile {
    0% { transform: translate(-50%, -50%) translateY(-130vh); }
    100% { transform: translate(-50%, -50%) translateY(130vh); }
  }

  .katana-img {
    height: 60vh;
    transform: rotate(90deg);
  }

  .trail {
    bottom: 50%;
    left: 50%;
    top: auto;
    right: auto;
    width: 200vw;
    height: 150vh;
    transform: translateX(-50%);
    background: linear-gradient(to bottom, transparent 0%, rgba(188, 19, 254, 0.4) 60%, rgba(0, 243, 255, 0.9) 100%);
    mask-image: linear-gradient(to bottom, transparent, black 80%);
    -webkit-mask-image: linear-gradient(to bottom, transparent, black 80%);
  }
}

/* ==================== */
/* Logo */
/* ==================== */
.logo-container {
  position: relative;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-beat {
  width: 100px;
  height: auto;
  animation: beat 0.8s ease-in-out infinite;
  filter: drop-shadow(0 0 15px rgba(249, 0, 251, 0.6));
  /* GPU acceleration */
  will-change: transform;
  backface-visibility: hidden;
}

@keyframes beat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

/* ==================== */
/* Default Loader Bar (under logo) */
/* ==================== */
.default-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
  width: 200px;
}
.loader-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}
.loader-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, #bc13fe, #00f3ff);
  border-radius: 2px;
  animation: shimmer 1.5s ease-in-out infinite;
}
@keyframes shimmer {
  0% { left: -40%; }
  100% { left: 100%; }
}
.loader-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  margin-top: 12px;
  letter-spacing: 0.5px;
}
.loader-percent {
  color: #00f3ff;
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: 4px;
}

/* ==================== */
/* Data Loading Indicator */
/* ==================== */
.data-loader-container {
  position: absolute;
  bottom: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 30;
}
.progress-ring {
  position: relative;
  width: 100px;
  height: 100px;
}
.progress-ring svg {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}
.progress-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 8;
}
.progress-bar {
  fill: none;
  stroke: url(#progress-gradient);
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 339.292;
  transition: stroke-dashoffset 0.3s ease;
}
.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.25rem;
  font-weight: bold;
  background: linear-gradient(135deg, #bc13fe, #00f3ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.loading-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}

/* ==================== */
/* Animation 3: Katana Slice (Shatter) */
/* ==================== */
.page-load-overlay.transparent-bg {
  background: transparent;
}

.katana-slice-container {
  position: absolute;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  z-index: 10;
  pointer-events: none;
}

.shard {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #030014;
  background-image: radial-gradient(circle at 50% 50%, #0d0525 0%, #030014 100%);
  background-size: cover;
  transition: transform 1.2s cubic-bezier(0.5, 0, 0.2, 1), opacity 1.2s ease;
  will-change: transform, opacity;
  box-sizing: border-box;
}

.shard::after {
  content: '';
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
  box-shadow: inset 0 0 0 1px rgba(0, 243, 255, 0.1);
}

.cut-line {
  position: absolute;
  height: 4px;
  width: 0;
  background: white;
  box-shadow: 0 0 10px #fff, 0 0 20px #00f3ff, 0 0 40px #bc13fe;
  transform-origin: left center;
  opacity: 0;
  z-index: 200;
  pointer-events: none;
  border-radius: 50%;
}

.cut-line.active {
  animation: flashCut 0.3s linear forwards;
}

.logo-container.hidden {
  opacity: 0;
}

/* Global Shake Effect */
:global(.shake-impact) {
  animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-4px, 2px, 0); }
  20%, 80% { transform: translate3d(8px, -4px, 0); }
  30%, 50%, 70% { transform: translate3d(-12px, 6px, 0); }
  40%, 60% { transform: translate3d(12px, -6px, 0); }
}

/* ==================== */
/* Fade Transition - GPU optimized */
/* ==================== */
.fade-enter-active {
  transition: none;
}
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<!-- Non-scoped styles for keyframes -->
<style>
@keyframes flashCut {
  0% { width: 0; opacity: 1; }
  40% { width: 150vw; opacity: 1; }
  100% { width: 150vw; opacity: 0; }
}
</style>
